/**
 * Which paths a session with NO tenant can use.
 *
 * Everything outside these scopes its requests to a tenant, and without one the server
 * refuses with *"Platform admin must specify X-Tenant-Id"* — correctly, since it will not
 * infer cross-tenant access. On screen that is a warning panel where a fleet should be,
 * which reads as a permissions fault rather than as standing in the wrong place.
 *
 * ⚠️ **Extracted from `routes.tsx` when a SECOND caller appeared**, and the state it was in
 * is the reason: the rule was private there and `routes.worksWithoutTenant.test.ts` RESTATED
 * it, so the case pinned a copy rather than the thing. A copy that drifts from its original
 * is the defect this codebase has recorded against `inferActiveTenant`, the per-channel
 * transport map, `Can`'s private `hasRole` and the command palette's private visibility
 * rule. The test drives this function now.
 */

/**
 * ⚠️ An ALLOW-LIST of prefixes, never a list of tenant-scoped routes.
 *
 * The tenant-scoped set is every detail page, every edit page and everything added later,
 * and an incomplete list of THOSE fails silently in the direction that keeps the bug. These
 * three are the whole of what a tenant-less session can use, so anything else is
 * tenant-scoped by construction.
 */
export const PLATFORM_ONLY_PREFIXES = ['/platform', '/partner', '/account'] as const;

/**
 * ⚠️ `/` is an EXACT match, never a prefix.
 *
 * Written first as one list including `'/'` and tested with `startsWith`, which matches
 * EVERY path — so the redirect could never fire and the guard was decorative. Caught by
 * reading it back rather than by running it, because the symptom is the previous behaviour
 * exactly.
 */
/**
 * Paths whose DATA has no tenant, so a tenant-less session may use them despite sitting
 * outside the platform prefixes.
 *
 * ⚠️ **This existed as a second, contradicting rule and the two disagreed.** `nav.tsx` marks
 * Prompt Registry `platformOwned` so a platform administrator is OFFERED it without entering
 * a tenant — its own comment says the page was otherwise "unreachable by navigation for the
 * only role that may author a prompt". The router then refused the same path and redirected
 * them back to `/`, so the menu item did nothing at all: reported as "showing the same page".
 *
 * ⚠️ EXACT matches, never prefixes. `/prompts` is one screen, not a section, and a prefix
 * would admit `/promptsomething` the day such a route exists.
 *
 * Kept HERE rather than derived from `NAV_ITEMS` because this module is imported by the auth
 * guard and `nav.tsx` pulls in every MUI icon in the product. `navVisibility.test.ts` asserts
 * the two agree, which is the same shape as `RESELLER_PARTNER_PATHS` mirroring
 * `@ResellerScoped()`.
 */
export const PLATFORM_OWNED_PATHS = ['/prompts'] as const;

export function worksWithoutTenant(pathname: string): boolean {
  if (pathname === '/') return true;
  if ((PLATFORM_OWNED_PATHS as readonly string[]).includes(pathname)) return true;
  return PLATFORM_ONLY_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Where a BARE section path lands.
 *
 * ⚠️ **Reported from the partner console: `/partner` rendered *Page not found***, on the
 * section that is a partner administrator's entire application. It is the loop guard's blind
 * spot rather than a missing link — `RequireAuth` redirects a tenant-less partner to their
 * console only when the path does NOT already start with `/partner`, which is correct or the
 * console would redirect to itself for ever. The bare path satisfies that prefix and had no
 * route, so it fell through to the catch-all.
 *
 * ⚠️ **Keyed by `PLATFORM_ONLY_PREFIXES` so a section cannot gain a prefix and keep the
 * hole.** All three had it; only one was reported, and fixing that one alone is how the next
 * one gets reported. A prefix test answers *am I inside this section*, never *is this a page*
 * — and the two are only the same thing while every section happens to have an index.
 */
export const SECTION_LANDING = {
  '/platform': '/platform/tenants',
  '/partner': '/partner/companies',
  '/account': '/account/notifications',
} as const satisfies Record<(typeof PLATFORM_ONLY_PREFIXES)[number], string>;
